import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, CurrencyDollar, TrendUp, Lightning, Warning, ChartBar, ChartLineUp, Plus, Trash } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import AppLayout from "@/components/AppLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={18} className="text-primary" />
        <span className="text-xs font-mono uppercase tracking-wide">{label}</span>
      </div>
      <p className="font-heading font-black text-3xl mt-3 tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [embeds, setEmbeds] = useState([]);
  const [embTitle, setEmbTitle] = useState("");
  const [embUrl, setEmbUrl] = useState("");
  const [embBusy, setEmbBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load analytics"))
      .finally(() => setLoading(false));
    api.get("/admin/posthog-embeds").then((r) => setEmbeds(r.data.embeds || [])).catch(() => {});
  }, []);

  const addEmbed = async () => {
    if (!embUrl.trim()) return;
    setEmbBusy(true);
    try {
      const { data: e } = await api.post("/admin/posthog-embeds", { title: embTitle, url: embUrl });
      setEmbeds((x) => [...x, e]);
      setEmbTitle(""); setEmbUrl("");
      toast.success("Insight embedded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add embed");
    } finally { setEmbBusy(false); }
  };

  const removeEmbed = async (id) => {
    try {
      await api.delete(`/admin/posthog-embeds/${id}`);
      setEmbeds((x) => x.filter((e) => e.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  if (loading) {
    return <AppLayout><div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></AppLayout>;
  }
  if (err) {
    return <AppLayout><div className="rounded-lg border border-dashed border-border p-16 text-center" data-testid="insights-error"><p className="text-muted-foreground">{err}</p></div></AppLayout>;
  }

  const t = data.totals;
  const maxFunnel = Math.max(...data.funnel.map((f) => f.count), 1);

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Insights</h1>
        <p className="text-muted-foreground mt-2">Your business at a glance — signups, activation and revenue.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="insights-stats">
        <Stat icon={Users} testid="stat-users" label="Total users" value={t.users} sub={`+${t.users_7d} in last 7 days`} />
        <Stat icon={ChartLineUp} testid="stat-mrr" label="MRR" value={`$${(t.mrr || 0).toLocaleString()}`} sub="monthly recurring" />
        <Stat icon={CurrencyDollar} testid="stat-revenue" label="Revenue (total)" value={`$${t.revenue_total.toLocaleString()}`} sub={`$${t.revenue_30d.toLocaleString()} in last 30 days`} />
        <Stat icon={TrendUp} testid="stat-customers" label="Paying customers" value={t.paying_customers} sub={`${t.paid_workspaces} paid workspaces`} />
        <Stat icon={Lightning} testid="stat-conversion" label="Conversion" value={`${t.conversion_rate}%`} sub="signup → paid" />
      </div>

      <div className="rounded-lg border border-border bg-card p-6 mt-6" data-testid="insights-revenue">
        <h2 className="font-heading font-bold text-lg mb-5">MRR trend · last 6 months</h2>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.revenue_series || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <Tooltip cursor={{ stroke: "hsl(var(--accent))" }}
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => [`$${v}`, "Revenue"]} />
            <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {/* Funnel */}
        <div className="rounded-lg border border-border bg-card p-6" data-testid="insights-funnel">
          <h2 className="font-heading font-bold text-lg mb-5">Conversion funnel</h2>
          <div className="space-y-4">
            {data.funnel.map((f, i) => {
              const pct = Math.round((f.count / maxFunnel) * 100);
              const rate = i === 0 ? 100 : Math.round((f.count / (data.funnel[0].count || 1)) * 100);
              return (
                <div key={f.stage} data-testid={`funnel-${f.stage.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium">{f.stage}</span>
                    <span className="text-muted-foreground font-mono">{f.count} · {rate}%</span>
                  </div>
                  <div className="h-3 rounded-full bg-secondary overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: i * 0.08, duration: 0.5 }}
                      className="h-full bg-primary rounded-full" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Signups chart */}
        <div className="rounded-lg border border-border bg-card p-6" data-testid="insights-signups">
          <h2 className="font-heading font-bold text-lg mb-5">Signups · last 14 days</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.signups_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={2} stroke="hsl(var(--muted-foreground))" />
              <Tooltip cursor={{ fill: "hsl(var(--accent))" }}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="signups" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent payments */}
      <div className="rounded-lg border border-border bg-card p-6 mt-6" data-testid="insights-payments">
        <h2 className="font-heading font-bold text-lg mb-4">Recent payments</h2>
        {data.recent_payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments yet.</p>
        ) : (
          <div className="space-y-2">
            {data.recent_payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="capitalize">{p.tier}</Badge>
                  <span className="text-muted-foreground">{p.interval} · {p.provider}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono font-semibold">${p.amount}</span>
                  <span className="text-xs text-muted-foreground">{p.date ? new Date(p.date).toLocaleDateString() : ""}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Cohort retention */}
      <div className="rounded-lg border border-border bg-card p-6 mt-6 overflow-x-auto" data-testid="insights-retention">
        <h2 className="font-heading font-bold text-lg mb-1">Weekly retention by cohort</h2>
        <p className="text-sm text-muted-foreground mb-5">Each row is users who signed up that week; cells show the % still active in later weeks.</p>
        {(!data.retention || data.retention.cohorts.length === 0) ? (
          <p className="text-sm text-muted-foreground" data-testid="retention-empty">Not enough signup history yet — cohorts appear as users join.</p>
        ) : (
          <table className="w-full text-sm border-collapse" data-testid="retention-table">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left font-medium py-2 pr-4 whitespace-nowrap">Cohort</th>
                <th className="text-left font-medium py-2 pr-4">Users</th>
                {Array.from({ length: data.retention.weeks }).map((_, i) => (
                  <th key={i} className="text-center font-medium py-2 px-2 whitespace-nowrap">W{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.retention.cohorts.map((row, ri) => (
                <tr key={ri} data-testid={`retention-row-${ri}`} className="border-t border-border">
                  <td className="py-2 pr-4 whitespace-nowrap font-medium">{row.week}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{row.size}</td>
                  {row.cells.map((cell) => (
                    <td key={cell.offset} className="py-1.5 px-1">
                      <div className="rounded text-center py-1.5 text-xs font-semibold"
                        style={{
                          backgroundColor: `hsl(var(--primary) / ${Math.max(cell.pct / 100, 0.06)})`,
                          color: cell.pct > 55 ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
                        }}>
                        {cell.pct}%
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Churn watch */}
      <div className="rounded-lg border border-border bg-card p-6 mt-6" data-testid="insights-churn">
        <div className="flex items-center gap-2 mb-1">
          <Warning size={20} weight="fill" className="text-amber-500" />
          <h2 className="font-heading font-bold text-lg">Churn watch</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Paying customers with no activity in the last 14 days — reach out before they cancel.</p>
        {(!data.churn_risk || data.churn_risk.length === 0) ? (
          <p className="text-sm text-muted-foreground" data-testid="churn-empty">🎉 No at-risk customers right now — everyone's active.</p>
        ) : (
          <div className="space-y-2">
            {data.churn_risk.map((c, i) => (
              <div key={i} data-testid={`churn-row-${i}`} className="flex items-center justify-between py-2.5 border-b border-border last:border-0 text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">{c.owner_name || c.owner_email || c.workspace}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.owner_email} · {c.workspace}</p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <Badge variant="secondary" className="capitalize">{c.plan}</Badge>
                  <span className="text-xs font-mono text-amber-500">
                    {c.days_quiet != null ? `${c.days_quiet}d quiet` : "no activity"}
                  </span>
                  {c.owner_email && (
                    <a href={`mailto:${c.owner_email}`} data-testid={`churn-email-${i}`}
                      className="text-primary hover:underline text-xs font-medium">Email</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PostHog embeds */}
      <div className="rounded-lg border border-border bg-card p-6 mt-6" data-testid="insights-posthog">
        <div className="flex items-center gap-2 mb-1">
          <ChartBar size={20} weight="fill" className="text-primary" />
          <h2 className="font-heading font-bold text-lg">PostHog funnels &amp; retention</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Embed live PostHog insights. In PostHog open an insight → <b>Share</b> → <b>Embed</b>, enable sharing, then paste the embed URL (or the whole &lt;iframe&gt; snippet) below.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <Input data-testid="ph-embed-title" placeholder="Label (e.g. Activation funnel)" value={embTitle}
            onChange={(e) => setEmbTitle(e.target.value)} className="sm:w-64" />
          <Input data-testid="ph-embed-url" placeholder="https://us.posthog.com/embedded/…" value={embUrl}
            onChange={(e) => setEmbUrl(e.target.value)} className="flex-1" />
          <Button data-testid="ph-embed-add" onClick={addEmbed} disabled={embBusy || !embUrl.trim()}>
            <Plus size={16} className="mr-1.5" /> Embed
          </Button>
        </div>
        {embeds.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="ph-embed-empty">No embedded insights yet. Add one above to see it here.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5">
            {embeds.map((e) => (
              <div key={e.id} data-testid={`ph-embed-${e.id}`} className="rounded-lg border border-border overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-secondary/40">
                  <span className="text-sm font-medium truncate">{e.title}</span>
                  <button data-testid={`ph-embed-delete-${e.id}`} onClick={() => removeEmbed(e.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors" title="Remove">
                    <Trash size={15} />
                  </button>
                </div>
                <iframe title={e.title} src={e.url} className="w-full h-[360px] bg-white" frameBorder="0" allowFullScreen />
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
